import { navigate, route, setNotFound, startRouter } from './router.js';
import { getPendingRegistration, isAuthenticated } from './auth/storage.js';
import { renderLogin } from './views/login.js';
import { renderVerify } from './views/verify.js';
import { renderHome, showHardUpdateGate } from './views/home.js';
import { renderChapter } from './views/chapter.js';
import { renderTopic } from './views/topic.js';
import { renderFavorites } from './views/favorites.js';
import { renderPhrases } from './views/phrases.js';
import { renderHowToLearn } from './views/howToLearn.js';
import { renderAbout } from './views/about.js';
import { renderPrivacy, renderTerms } from './views/legal.js';
import { showLoader } from './components/feedback.js';

const mount = document.getElementById('app');

/* ---- Routes with auth guards -------------------------------------------- */

/**
 * Wrap a screen so it only renders for a signed-in user; anyone else is sent to
 * sign-in. `params` carries the route's `:name` values, so a detail screen reads
 * its id from there.
 */
const guarded = (render) => (params) => {
  if (!isAuthenticated()) return navigate('/login');
  render(mount, params);
};

// Home — requires a stored access token.
route('/', guarded(renderHome));
// Chapter detail — the topics list for a chapter opened from home.
route(
  '/chapter/:id',
  guarded((el, { id }) => renderChapter(el, id))
);
// Topic detail — the dialogue, phrases and audio for a single topic.
route(
  '/topic/:id',
  guarded((el, { id }) => renderTopic(el, id))
);
// Authenticated sections reachable from the hamburger menu.
route('/favorites', guarded(renderFavorites));
route('/phrases', guarded(renderPhrases));
route('/jak-sie-uczyc', guarded(renderHowToLearn));
route('/o-aplikacji', guarded(renderAbout));

// Legal documents — deliberately **unguarded**: the login footer links to them,
// so they have to open before there is a session, and the "O aplikacji" screen
// links to the same routes afterwards. The view picks its own chrome (app shell
// vs. bare screen) from `isAuthenticated()`.
route('/regulamin', () => renderTerms(mount));
route('/polityka-prywatnosci', () => renderPrivacy(mount));

// Step 1 — e-mail. Authenticated users skip straight to home.
route('/login', () => {
  if (isAuthenticated()) return navigate('/');
  renderLogin(mount);
});

// Step 2 — code. Requires a pending registration from step 1.
route('/verify', () => {
  if (isAuthenticated()) return navigate('/');
  if (!getPendingRegistration()) return navigate('/login');
  renderVerify(mount);
});

setNotFound(() => navigate('/'));

export function startApp() {
  // A required update recorded on an earlier run blocks the whole app, offline
  // included: the router never starts, so no route can render behind the gate.
  // The record is tagged with the build it blocked, so a newer bundle clears it
  // by itself — see `showHardUpdateGate` in `views/home.js`.
  if (showHardUpdateGate()) return;

  // Show the global loader for one paint, then resolve the initial route.
  showLoader(mount, 'Ładowanie aplikacji…');
  requestAnimationFrame(startRouter);
}
